/**
 * Between the offering forms' pricing shape and the Pricing domain.
 * Pure TypeScript - no React imports.
 *
 * The offering forms predate this slice and encode pricing their own way: the Pricing
 * Model as `priceType`, where Single Fee is spelled `paid`, and a currency row as
 * `{callPriceCurrency, callPrice, minCallPrice}`. That encoding is as external to the
 * domain as the wire is, so it is translated here rather than leaking into the rules.
 *
 * All four offering types share this translation, which is why it lives in the slice
 * rather than beside any one form.
 */

import {
  applyPricingModelTransition,
  emptyPricingCurrencyRow,
  type PricingCurrencyRow,
  type PricingModel,
} from "../../domain";

/**
 * The offering forms' encoding of the Pricing Model. `paid` is Single Fee, spelled the
 * way the forms have always spelled it.
 */
export type OfferingFormPriceType = "free" | "paid" | "pwyw";

/**
 * One currency row as an offering form holds it. `callPrice` carries the Suggested Price
 * under Pay What You Want and the fixed price under Single Fee — the same field, which is
 * why amounts survive a switch between those two models.
 *
 * Amounts are optional because a row exists from the moment the coach adds it, before
 * they have typed anything into it.
 */
export interface OfferingFormCurrencyRow {
  callPriceCurrency: string;
  callPrice?: number;
  minCallPrice?: number;
}

export const OFFERING_FORM_PRICE_TYPE_BY_PRICING_MODEL: Readonly<
  Record<PricingModel, OfferingFormPriceType>
> = {
  free: "free",
  "single-fee": "paid",
  pwyw: "pwyw",
};

const PRICING_MODEL_BY_OFFERING_FORM_PRICE_TYPE: Readonly<
  Record<OfferingFormPriceType, PricingModel>
> = {
  free: "free",
  paid: "single-fee",
  pwyw: "pwyw",
};

/**
 * Reads a form's `priceType` as a Pricing Model.
 *
 * @param priceType - the form's value, which may be absent before the coach has chosen
 * @returns the Pricing Model, defaulting to Free
 */
export function toPricingModel(priceType: string | undefined): PricingModel {
  return (
    PRICING_MODEL_BY_OFFERING_FORM_PRICE_TYPE[priceType as OfferingFormPriceType] ??
    "free"
  );
}

/**
 * Writes a Pricing Model back in the form's own encoding.
 */
export function toOfferingFormPriceType(model: PricingModel): OfferingFormPriceType {
  return OFFERING_FORM_PRICE_TYPE_BY_PRICING_MODEL[model];
}

/**
 * Reads one form row as a domain currency row.
 *
 * A half-typed amount arrives from the DOM as `""` or `NaN`; both mean the coach has not
 * entered a number yet, so neither becomes an amount the rules would judge.
 */
export function toPricingCurrencyRow(row: OfferingFormCurrencyRow): PricingCurrencyRow {
  return {
    currency: row.callPriceCurrency,
    minimum: toEnteredAmount(row.minCallPrice),
    suggested: toEnteredAmount(row.callPrice),
  };
}

/**
 * Writes a domain currency row back in the form's own shape.
 */
export function toOfferingFormCurrencyRow(
  row: PricingCurrencyRow
): OfferingFormCurrencyRow {
  return {
    callPriceCurrency: row.currency,
    callPrice: row.suggested,
    minCallPrice: row.minimum,
  };
}

/**
 * An unfilled row, ready for the coach to pick a currency and enter amounts.
 */
export function emptyOfferingFormCurrencyRow(): OfferingFormCurrencyRow {
  return toOfferingFormCurrencyRow(emptyPricingCurrencyRow());
}

/**
 * Reshapes a form's currency rows for a change of Pricing Model.
 *
 * Returns `null` when this feature has no opinion. Free ↔ Single Fee behaved a certain
 * way long before this slice existed, and moving those rows is not what this feature is
 * for — a caller that gets `null` should leave the form exactly as it found it.
 *
 * @param from - the `priceType` the coach is leaving
 * @param to - the `priceType` the coach has chosen
 * @param rows - the form's current rows
 * @returns the rows the form should hold, or `null` to leave them alone
 */
export function transitionOfferingFormCurrencyRows(
  from: OfferingFormPriceType,
  to: OfferingFormPriceType,
  rows: OfferingFormCurrencyRow[]
): OfferingFormCurrencyRow[] | null {
  const involvesPayWhatYouWant = from === "pwyw" || to === "pwyw";

  // Nothing to say about a move this feature did not introduce, whatever rows the coach
  // is holding — including none. Live events and booking services keep their base
  // currency in the form's scalars, so an empty array is the ordinary state of a
  // perfectly complete Free or Single Fee offering, not a form with no pricing in it.
  if (from === to || !involvesPayWhatYouWant) {
    return null;
  }

  const next = applyPricingModelTransition(
    toPricingModel(from),
    toPricingModel(to),
    rows.map(toPricingCurrencyRow)
  ).map(toOfferingFormCurrencyRow);

  // Under Pay What You Want the rows are the only place pricing lives, so a form holding
  // none of them cannot be completed and cannot be recovered from: the currency picker
  // never offers the base currency back. Leave one blank row behind for the way back.
  if (next.length === 0) {
    return [emptyOfferingFormCurrencyRow()];
  }

  return next;
}

function toEnteredAmount(amount: number | undefined): number | undefined {
  return typeof amount === "number" && Number.isFinite(amount) ? amount : undefined;
}
